import React, { type PropsWithChildren } from 'react';
import { type StyleProp, StyleSheet, View, type ViewStyle } from 'react-native';
import Animated, {
  type AnimatedRef,
  Extrapolation,
  interpolate,
  useAnimatedRef,
  useAnimatedStyle,
  useScrollOffset,
} from 'react-native-reanimated';

import { type useA11ySelector } from '@sg/a11y-helpers';
import { theme, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchScrollView = (props: Props) => {
  const { ref, style, scrollViewStyle, contentContainerStyle, a11ySelector, children } = props;

  const { minWidth } = useResponsive();

  // ─── Refs ──────────────────────────────────────────────────────────────

  const ownScrollViewRef = useAnimatedRef<Animated.ScrollView>();

  // ─── Derived ───────────────────────────────────────────────────────────

  const scrollViewRef = ref ?? ownScrollViewRef;
  const scrollOffset = useScrollOffset(scrollViewRef);
  const shouldShowScrollTopBorder = minWidth.isSM;

  // ─── Styles ────────────────────────────────────────────────────────────

  const containerStyles = [styles.container, style];
  const scrollViewStyles = [styles.scrollView, scrollViewStyle];

  const scrollTopBorderAnimatedStyles = useAnimatedStyle(() => {
    return {
      opacity: interpolate(
        scrollOffset.value,
        [0, SCROLL_TOP_BORDER_FADE_DISTANCE],
        [0, 1],
        Extrapolation.CLAMP,
      ),
    };
  });

  const scrollTopBorderStyles = [styles.scrollTopBorder, scrollTopBorderAnimatedStyles];

  return (
    <View style={containerStyles} {...a11ySelector?.props}>
      {shouldShowScrollTopBorder ? (
        <Animated.View style={scrollTopBorderStyles} pointerEvents="none" />
      ) : null}

      <Animated.ScrollView
        ref={scrollViewRef}
        style={scrollViewStyles}
        contentContainerStyle={contentContainerStyle}
        keyboardShouldPersistTaps="handled"
      >
        {children}
      </Animated.ScrollView>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SCROLL_TOP_BORDER_FADE_DISTANCE = theme.spacing['2'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollTopBorder: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    zIndex: 3,
    borderTopWidth: 1,
    borderTopColor: theme.colors.GRAY,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  ref?: AnimatedRef<Animated.ScrollView>;
  style?: StyleProp<ViewStyle>;
  scrollViewStyle?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  a11ySelector?: ReturnType<typeof useA11ySelector>;
}>;
